import clsx from 'clsx';

export function SitePreviewPlaceholder({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div className={clsx('@container size-full overflow-hidden bg-base', className)} {...props}>
            <PlaceholderHeader />

            <div className="mx-auto w-full max-w-screen-2xl @5xl:p-8 px-4">
                <div className="flex items-stretch">
                    <PlaceholderSidebar />

                    <div className="flex flex-1 flex-col gap-4 pt-8">
                        <PlaceholderContent />
                        <PlaceholderCards />
                    </div>

                    <PlaceholderRightSidebar />
                </div>
            </div>
        </div>
    );
}

/**
 * Page header placeholder
 */
function PlaceholderHeader() {
    return (
        <div className="mx-auto w-full max-w-screen-2xl border-b-2 p-4 @5xl:px-8">
            <div className="flex @5xl:gap-12">
                <div className="@5xl:basis-72 basis-1/2">
                    <div className="shimmer h-9 w-3/4 rounded-xl bg-standout-side" />
                </div>

                <div className="flex flex-1 items-center justify-end @5xl:justify-between">
                    <div className="shimmer @5xl:block hidden h-9 basis-2/5 rounded-xl bg-standout-side" />

                    <div className="shimmer h-9 @xl:basis-24 basis-1/2 rounded-xl bg-standout-side" />
                </div>
            </div>
        </div>
    );
}

const SIDEBAR_ITEMS: string[] = Array.from({ length: 10 }, (_, index) => `sidebar-${index}`);

/**
 * Page sidebar placeholder
 */
function PlaceholderSidebar() {
    return (
        <div className="mr-12 @5xl:flex hidden w-full basis-72 flex-col gap-6 py-8">
            {SIDEBAR_ITEMS.map((id, index) => (
                <div
                    key={id}
                    className={clsx(
                        'shimmer h-4 rounded-xl bg-standout-side',
                        index % 2 === 0 ? 'w-3/4' : 'w-1/2'
                    )}
                />
            ))}
        </div>
    );
}

const CONTENT_LINES: string[] = Array.from({ length: 4 }, (_, index) => `line-${index}`);

/**
 * Content placeholder
 */
function PlaceholderContent() {
    return (
        <div className="flex flex-col gap-8">
            <div className="shimmer h-7 w-1/3 rounded-xl bg-standout-side" />
            <div className="flex flex-col">
                {CONTENT_LINES.map((id, index) => (
                    <div
                        key={id}
                        className={clsx(
                            'shimmer h-4 rounded-xl bg-standout-side',
                            index % 2 === 0 ? 'mb-4 w-full' : 'mb-8 w-1/2'
                        )}
                    />
                ))}
            </div>
        </div>
    );
}

const CARD_ITEMS: string[] = Array.from({ length: 6 }, (_, index) => `card-${index}`);

/**
 * Cards placeholder
 */
function PlaceholderCards() {
    return (
        <div className="grid flex-1 @5xl:grid-cols-3 @lg:grid-cols-2 grid-cols-1 @5xl:gap-6 gap-4">
            {CARD_ITEMS.map((id) => (
                <div
                    key={id}
                    className="shimmer @5xl:aspect-square aspect-video rounded-xl bg-standout-side"
                />
            ))}
        </div>
    );
}

const RIGHT_SIDEBAR_ITEMS: string[] = Array.from(
    { length: 4 },
    (_, index) => `right-sidebar-${index}`
);

/**
 * Page TOC placeholder
 */
function PlaceholderRightSidebar() {
    return (
        <div className="ml-12 @6xl:flex hidden w-full basis-56 flex-col gap-6 py-8">
            {RIGHT_SIDEBAR_ITEMS.map((id, index) => (
                <div
                    key={id}
                    className={clsx(
                        'shimmer h-4 rounded-xl bg-standout-side',
                        index % 2 === 0 ? 'w-3/4' : 'w-1/2'
                    )}
                />
            ))}
        </div>
    );
}
